import { useState } from 'react';
import { dashboardApi } from '@/services/dashboardApi';
import { useLoad, Loading, Kpi, Block, eur, num, pct, MONTHS, inputCls, yearOptions } from './ui';

// Cuadro de dirección (antes CM_DIRECCION) con selector Mes / Trimestre / Año.
export default function CdmResumen({ cdm }) {
  const rules = cdm?.rules || {};
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [mode, setMode] = useState('Año');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const { data, error } = useLoad(() => dashboardApi.cdmSummary(year, mode, month), [year, mode, month]);

  const label = data ? (mode === 'Año' ? `Año ${year}` : mode === 'Trimestre'
    ? `${MONTHS[data.period.label.from - 1]}–${MONTHS[data.period.label.to - 1]} ${year}` : `${MONTHS[month - 1]} ${year}`) : '';

  return (
    <div className="space-y-6" data-testid="cdm-resumen">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-1">
          {['Mes', 'Trimestre', 'Año'].map((m) => (
            <button key={m} onClick={() => setMode(m)} className={`px-3 py-1.5 rounded-lg text-sm ${mode === m ? 'bg-[#c9a84c]/15 text-[#c9a84c]' : 'text-gray-400'}`}>{m}</button>
          ))}
        </div>
        {mode !== 'Año' && (
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={inputCls} aria-label="Mes">
            {MONTHS.map((m, i) => <option key={m} value={i + 1} className="bg-[#0d1829]">{m}</option>)}
          </select>
        )}
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={inputCls} aria-label="Año">
          {yearOptions(cdm).map((y) => <option key={y} value={y} className="bg-[#0d1829]">{y}</option>)}
        </select>
        {data && (
          <p className="text-xs text-gray-400 ml-auto">
            Datos calculados el {new Date(data.data_as_of.computed_at).toLocaleString('es-ES')} · último apunte {data.data_as_of.last_record_date || '—'}
          </p>
        )}
      </div>

      {!data ? <Loading error={error} /> : (
        <>
          {data.pending > 0 && (
            <p className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] px-4 py-3 text-sm text-amber-100">
              Hay {data.pending} asuntos pendientes de revisar (ventas sin broker, cobros sin apunte…). Están en la pestaña «Pendientes».
            </p>
          )}
          <Block title={`Finanzas · ${label}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Ingresos cobrados" value={eur(data.period.income)} sub="Caja real, sin financiación" />
              <Kpi label="Gastos" value={eur(data.period.expenses)} />
              <Kpi label="Resultado" value={eur(data.period.result)} tone={data.period.result < 0 ? 'bad' : 'good'} sub={`Margen ${pct(data.period.margin)}`} />
              <Kpi label="Caja a cierre" value={eur(data.period.cash)} sub="Incluye aportaciones de capital" />
              <Kpi label="Autonomía de caja (meses)" value={`${num(data.period.runway, 1)} meses`} sub={`Coste comprometido ${eur(data.period.committed_monthly)}/mes`} tone={data.period.runway < 3 ? 'bad' : undefined} />
              <Kpi label="Pendiente de cobro" value={eur(data.period.pending_collection)} sub="Reservas y aplazados" />
              <Kpi label="Comisiones FHCT" value={eur(data.period.fhct_commissions)} sub="Coste variable" />
              <Kpi label="Disparador CFO" value={data.period.cfo_trigger ? 'Sí' : 'No'} sub={rules.cfo_trigger_margin != null ? `Margen ≥ ${num(rules.cfo_trigger_margin * 100)} %` : undefined} />
            </div>
          </Block>
          <Block title={`Comercial · ${label}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Altas de membresía" value={num(data.period.altas)} sub={`Access ${data.period.altas_access} · PRO ${data.period.altas_pro} · Premium ${data.period.altas_premium}`} />
              <Kpi label="Volumen de membresías" value={eur(data.period.volume)} sub={`Ticket medio ${eur(data.period.ticket)}`} />
              <Kpi label="Cobrado en el periodo" value={eur(data.period.collected)} sub="Todo lo cobrado" />
              <Kpi label="Cobrado en membresías" value={eur(data.period.collected_membership_cum)} sub="Acumulado del año" />
              <Kpi label="FHCT vendidos" value={num(data.period.fhct_sold)} sub={`${data.period.fhct_purchases} compras · ${eur(data.period.fhct_contribution)}`} />
              <Kpi label="Bricks emitidos" value={num(data.period.bricks_issued, 1)} />
              <Kpi label="Brokers con venta" value={num(data.period.brokers_selling)} sub={`${data.period.brokers_new} brokers nuevos`} />
              <Kpi label="Altas con bootcamp" value={num(data.period.altas_bootcamp)} />
            </div>
          </Block>
          <Block title="FHCT y bricks · hoy">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="FHCT en cartera" value={num(data.fhct.units_active)} sub={`Histórico ${num(data.fhct.units_historic)}`} />
              <Kpi label="Capital en dinero real" value={eur(data.fhct.capital_real_eur)} sub="FHCT pagados con dinero real" />
              <Kpi label="Capital aportado" value={eur(data.fhct.capital_contributed_eur)} sub="Todas las aportaciones vigentes" />
              <Kpi label="Pendiente de emitir" value={num(data.fhct.pending_issue_units)} sub="Cobrado, aún sin emitir" />
              <Kpi label="Bricks vigentes" value={num(data.fhct.bricks_active, 1)} sub={`Socios ${num(data.fhct.bricks_members, 1)} · brokers ${num(data.fhct.bricks_brokers, 1)}`} />
              <Kpi label="Bricks manuales" value={num(data.fhct.bricks_manual, 1)} />
              <Kpi label="Bricks revocados" value={num(data.fhct.bricks_revoked, 1)} />
              <Kpi label="Brokers en Grandes cuentas" value={num(data.fhct.brokers_top_level)} sub={`${data.fhct.holders_institutional} socios en Plan Institucional`} />
            </div>
          </Block>
          <Block title="Comisiones · hoy">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Devengado" value={eur(data.commissions.accrued_eur)} sub={`Respaldado con dinero real ${eur(data.commissions.backed_eur)}`} />
              <Kpi label="Pagado" value={eur(data.commissions.paid_eur)} />
              <Kpi label="Exigible sin pagar" value={eur(data.commissions.payable_unpaid_eur)} tone={data.commissions.payable_unpaid_eur > 0 ? 'bad' : undefined} />
              <Kpi label="Aún no exigible" value={eur(data.commissions.not_yet_payable_eur)} sub={`Falta 100 % del pago, T&C o ${rules.commission_payable_days ?? 15} días`} />
            </div>
            <p className="text-xs text-gray-400">
              Pagado según el panel {eur(data.commissions.paid_eur)} · registrado en gastos (partida Comisiones) {eur(data.commissions_vs_expenses.registered_in_expenses_eur)} · diferencia {eur(data.commissions_vs_expenses.difference_paid_vs_expenses_eur)}
            </p>
          </Block>
        </>
      )}
    </div>
  );
}
